import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
} from "react-router-dom";
import Header from "./components/Header";
import Nav from "./components/Nav";
import Showcase from "./components/Showcase";
import Card from "./components/Card";
import Home from "./components/Home";
import Projects from "./components/Projects/Projects";
import Contacts from "./components/Contacts";
import Login from "./components/Login";
import AdminDashboard from "./components/AdminDashboard";
import ProjectDetail from "./components/Projects/ProjectDetail";
import SpanishExam from "./components/Study/Spanish/SpanishExam";
import ExamPage from "./components/Study/Spanish/ExamPage";
import VocabularyPage from "./components/Study/Spanish/VocabularyPage";
import StudyDashboard from "./components/Study/StudyDashboard";
import MathStudy from "./components/Study/Math/MathStudy";
import TheologyPage from "./components/Study/Theology/TheologyPage";
import CSStudy from "./components/Study/CS/CSStudy";
import CSCategoryIndex from "./components/Study/CS/CSCategoryIndex";
import { LanguageProvider } from "./contexts/LanguageContext";

function App() {
  const [user, setUser] = useState(() => {
    try {
      const storedUser = localStorage.getItem("user");
      const expiresAt = Number(localStorage.getItem("admin-session-expires-at") || 0);
      if (expiresAt && expiresAt <= Date.now()) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        localStorage.removeItem("admin-session-expires-at");
        return null;
      }
      return storedUser ? JSON.parse(storedUser) : null;
    } catch (_) {
      return null;
    }
  });
  const [projects, setProjects] = useState([]);
  const API_BASE = process.env.REACT_APP_API_BASE || `http://${window.location.hostname}:3001`;

  // Pre-warm the server on app load to reduce cold-start delay
  useEffect(() => {
    fetch(`${API_BASE}/api/ping`).catch(() => { });
  }, [API_BASE]);

  // --------------Below for projects-------------
  useEffect(() => {
    // SWR: show cached data immediately, then refresh in background
    const cached = localStorage.getItem('projects');
    if (cached) {
      try { setProjects(JSON.parse(cached)); } catch (e) { }
    }
    fetch(`${API_BASE}/api/projects`)
      .then((res) => res.json())
      .then((data) => {
        setProjects(data);
        localStorage.setItem('projects', JSON.stringify(data));
      })
      .catch((err) => console.error("Failed to load projects:", err));
  }, [API_BASE]);

  const addProject = (newProject) => {
    setProjects((prev) => {
      const updated = [newProject, ...prev];
      localStorage.setItem("projects", JSON.stringify(updated));
      return updated;
    });
  };

  const deleteProject = async (id) => {
    if (!window.confirm("Are you sure you want to delete this project?"))
      return;
    const res = await fetch(`${API_BASE}/api/projects/${id}`, {
      method: "DELETE",
    });
    if (res.ok) {
      setProjects((prev) => {
        const updated = prev.filter((p) => p._id !== id);
        localStorage.setItem("projects", JSON.stringify(updated));
        return updated;
      });
    }
  };

  const updateProject = (updatedProject) => {
    setProjects((prev) => {
      const updated = prev.map((p) =>
        p._id === updatedProject._id ? updatedProject : p
      );
      localStorage.setItem("projects", JSON.stringify(updated));
      return updated;
    });
  };
  //---------------------------------

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token || !window.location.pathname.includes("/admin")) return;

    const expiresAt = Number(localStorage.getItem("admin-session-expires-at") || 0);
    if (expiresAt && expiresAt <= Date.now()) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("admin-session-expires-at");
      window.location.href = "/login";
      return;
    }

    const logoutForExpiredSession = () => {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("admin-session-expires-at");
      setUser(null);
      window.location.href = "/login";
    };

    const timeout = expiresAt
      ? setTimeout(logoutForExpiredSession, Math.max(0, expiresAt - Date.now()))
      : null;

    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE}/api/users/validate-token`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!data.valid) {
          alert("Session expired. Please log in again.");
          logoutForExpiredSession();
        }
      } catch (err) {
        console.error("Token check failed:", err);
      }
    }, 60 * 1000);

    return () => {
      clearInterval(interval);
      if (timeout) clearTimeout(timeout);
    };
  }, [API_BASE]);

  useEffect(() => {
    if (!window.location.pathname.includes("/admin")) return;

    let timer;
    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        alert("You have been logged out due to inactivity.");
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        window.location.href = "/login";
      }, 24 * 60 * 60 * 1000);
    };

    window.addEventListener("mousemove", resetTimer);
    window.addEventListener("keypress", resetTimer);
    resetTimer();

    return () => {
      window.removeEventListener("mousemove", resetTimer);
      window.removeEventListener("keypress", resetTimer);
      clearTimeout(timer);
    };
  }, []);

  return (
    <LanguageProvider>
      <Router>
        <AppContent
          user={user}
          setUser={setUser}
          projects={projects}
          addProject={addProject}
          deleteProject={deleteProject}
          updateProject={updateProject}
        />
      </Router>
    </LanguageProvider>
  );
}

function AppContent({
  user,
  setUser,
  projects,
  addProject,
  deleteProject,
  updateProject,
}) {
  const MOBILE_BREAKPOINT = 900;
  const [isMobile, setIsMobile] = React.useState(
    window.innerWidth <= MOBILE_BREAKPOINT
  );
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <Routes>
      <Route
        path="/login"
        element={
          !user ? (
            <Login setUser={setUser} />
          ) : user?.isAdmin ? (
            <Navigate to="/admin" replace />
          ) : (
            <Navigate to="/" replace />
          )
        }
      />

      <Route
        path="/admin"
        element={
          user?.isAdmin ? (
            <AdminDashboard
              user={user || []}
              onNewProject={addProject}
              onUpdateProject={updateProject}
              projects={projects || []}
              onDeleteProject={deleteProject}
            />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      <Route
        path="/*"
        element={
          <div className={`container ${isMobile ? "block" : "block md:grid"}`}>
            <Card
              noAnimate
              extra={`col-span-2 bible-card${isMobile ? " mobile-slogan-card" : ""}`}
              style={{ position: "relative" }}
            >
              <Nav vocabularyOnly mobile={isMobile} setUser={setUser} user={user} />
              <div
                className={
                  isMobile
                    ? "flex flex-col text-center"
                    : "flex flex-col text-center"
                }
              >
                <div className="mx-3">
                  诗篇 23:1 - 耶和华是我的牧者，我必不至缺乏
                </div>
                <div className="mx-3">
                  Psalm 23:1 - The Lord is my shepherd; I shall not want.
                </div>
              </div>
            </Card>

            {isMobile && (
              <div className="mobile-top-nav w-full">
                <Header user={user} setUser={setUser} small />
                <Nav mobile hideVocabulary setUser={setUser} user={user} />
              </div>
            )}
            {!isMobile && (
              <div className="flex flex-col h-full gap-y-1 hidden md:flex">
                <Header user={user} setUser={setUser} />
                <Nav hideVocabulary setUser={setUser} user={user} />
                <Card
                  noAnimate
                  extra="bg-gray-100 dark:bg-gray-800 border-0 shadow-none py-4 h-[95px] text-center justify-center footer-rights-card"
                >
                  <div className="text-center leading-5">
                    <p className="text-[11px] text-[var(--text)]">© 2026 UZHISHI · All Rights Reserved</p>
                    <p className="text-[11px] text-[var(--text)]">Made by Danny</p>
                  </div>
                </Card>
              </div>
            )}

            <Showcase
              projects={projects}
            >
              <Routes>
                <Route path="/" element={<Home projects={projects} />} />
                <Route path="/projects" element={<Projects />} />
                <Route
                  path="/projects/:id"
                  element={<ProjectDetailWrapper />}
                />
                <Route path="/study" element={<StudyDashboard />} />
                <Route path="/study/theology" element={<TheologyPage />} />
                <Route path="/study/math" element={<MathStudy />} />
                <Route path="/study/math/course/:courseId" element={<MathStudy />} />
                <Route path="/study/math/:section" element={<MathStudy />} />
                <Route path="/study/math/:section/:year" element={<MathStudy />} />
                <Route path="/study/spanish" element={<SpanishExam />} />
                <Route path="/study/spanish/exam/:id" element={<ExamPage />} />
                <Route path="/study/spanish/vocabulary/:id" element={<VocabularyPage />} />
                <Route path="/study/cs" element={<CSCategoryIndex />} />
                <Route path="/study/cs/:subject" element={<CSStudy />} />
                <Route path="/contacts" element={<Contacts />} />
              </Routes>
            </Showcase>
          </div>
        }
      />
    </Routes>
  );
}

function ProjectDetailWrapper() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const API_BASE = process.env.REACT_APP_API_BASE || `http://${window.location.hostname}:3001`;

  useEffect(() => {
    // SWR: show cached project instantly, then refresh in background
    const cacheKey = `project-detail-${id}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      try {
        setProject(JSON.parse(cached));
        setLoading(false);
      } catch (e) { }
    }
    fetch(`${API_BASE}/api/projects/${id}`)
      .then((res) => res.json())
      .then((data) => {
        const p = Array.isArray(data) ? data[0] : data;
        setProject(p);
        setLoading(false);
        localStorage.setItem(cacheKey, JSON.stringify(p));
      })
      .catch((err) => {
        console.error("❌ Failed to load project:", err);
        setLoading(false);
      });
  }, [API_BASE, id]);

  if (loading)
    return (
      <div className="p-4 animate-pulse">
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4" />
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3 mb-2" />
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-6" />
        <div className="h-48 bg-gray-200 dark:bg-gray-700 rounded-xl mb-4" />
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2" />
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-4/5 mb-2" />
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/5" />
      </div>
    );

  if (!project || !project._id)
    return (
      <div className="text-center text-[var(--primary)] p-6 text-[12px] md:text-base">
        Project not found or deleted.
      </div>
    );

  return (
    <div className="w-full">
      <ProjectDetail project={project} editable={false} />
    </div>
  );
}

export default App;
